import type { ReactNode } from "react";

import {
  type ButtonShape,
  type ButtonSize,
  type ButtonVariant,
  buttonClassName,
} from "@/components/motion/button";
import { ButtonRouterLink } from "@/components/motion/button/router-link";
import { ExpandingArrowButton } from "@/components/motion/expanding-arrow-button";
import { ExpandingArrowRouterLink } from "@/components/motion/expanding-arrow-router-link";
import { useMaintenanceMode } from "@/features/maintenance/hooks";

/**
 * A call to action that leads into sign-in.
 *
 * While the maintenance window is open, sign-in cannot work, so the CTA renders
 * visibly disabled instead of as a link that silently bounces back to `/`. It
 * flips back to a normal link on its own once the server reports the window
 * closed, with no rebuild.
 *
 * `appearance="expanding-arrow"` is the hero treatment; it has one look, so
 * `variant`, `size` and `shape` do not apply to it.
 */
export function LoginCta({
  appearance = "button",
  variant,
  size,
  shape,
  className,
  children,
}: {
  appearance?: "button" | "expanding-arrow";
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  className?: string;
  children: ReactNode;
}) {
  const maintenanceMode = useMaintenanceMode();

  if (appearance === "expanding-arrow") {
    return maintenanceMode ? (
      <ExpandingArrowButton
        disabled
        title="Unavailable during maintenance"
        data-maintenance-disabled=""
        className={className}
      >
        {children}
      </ExpandingArrowButton>
    ) : (
      <ExpandingArrowRouterLink to="/login" preload="intent" className={className}>
        {children}
      </ExpandingArrowRouterLink>
    );
  }

  if (maintenanceMode) {
    return (
      <span
        role="link"
        aria-disabled="true"
        title="Unavailable during maintenance"
        data-maintenance-disabled=""
        className={buttonClassName({
          variant,
          size,
          shape,
          className: `${className ?? ""} pointer-events-none cursor-not-allowed opacity-40`,
        })}
      >
        {children}
      </span>
    );
  }

  return (
    <ButtonRouterLink
      to="/login"
      preload="intent"
      variant={variant}
      size={size}
      shape={shape}
      className={className}
    >
      {children}
    </ButtonRouterLink>
  );
}
